import type { components } from '@/lib/openapi.generated'
import type { PolicyPlacement } from '@/modules/hierarchy'
import { isRecord, json, nullableString, request, requireArray, requireNumber, requireString } from '@/lib/api'

type Schemas = components['schemas']
export type PreferredCounters = Schemas['PreferredCountersPolicyValue']
export type PreferredCounterPolicy = Omit<Schemas['PrinterPolicyDefinitionResponse'], 'selectorGui'> & {
  selectorGui: Record<string, unknown> | null
}
export type PreferredCounterEdit = Omit<PreferredCounterPolicy, 'id' | 'revision' | 'priority'> & { placement?: PolicyPlacement }
export const PREFERRED_COUNTER_POLICY_TYPE = 'printer:preferred-counters'

function strings(raw: unknown, field: string) {
  return requireArray(raw, field, (item) => requireString(item, field))
}

function decodePolicy(raw: unknown): PreferredCounterPolicy {
  if (!isRecord(raw) || !isRecord(raw.value)) throw new Error('Malformed preferred counters policy')
  if (raw.selectorGui !== null && raw.selectorGui !== undefined && !isRecord(raw.selectorGui)) {
    throw new Error('Malformed policy selector UI state')
  }
  return {
    id: requireString(raw.id, 'id'),
    name: requireString(raw.name, 'name'),
    value: {
      formatPriority: strings(raw.value.formatPriority, 'formatPriority'),
      countersToDisplay: strings(raw.value.countersToDisplay, 'countersToDisplay'),
    },
    selectorExpression: nullableString(raw.selectorExpression, 'selectorExpression'),
    selectorGui: raw.selectorGui ?? null,
    revision: requireNumber(raw.revision, 'revision'),
    priority: requireNumber(raw.priority, 'priority'),
  } satisfies Schemas['PrinterPolicyDefinitionResponse']
}

function decodeReceipt(raw: unknown) {
  if (!isRecord(raw)) throw new Error('Malformed preferred counters policy receipt')
  return {
    policyId: requireString(raw.policyId, 'policyId'),
    revision: requireNumber(raw.revision, 'revision'),
  }
}

const collection = (entityId: string) => `/api/v1/entities/${entityId}/printer-counter-policies`
const detail = (entityId: string, id: string) => `${collection(entityId)}/${id}`
export const preferredCounterPolicyApi = {
  get: (entityId: string, id: string, signal?: AbortSignal) => request(detail(entityId, id), decodePolicy, { signal }),
  create: (entityId: string, edit: PreferredCounterEdit, operationId: string) => request(
    collection(entityId), decodeReceipt,
    json('POST', { ...edit, operationId, placement: edit.placement ? { ...edit.placement, atStart: false } : undefined } satisfies Schemas['CreatePrinterPolicyBody']),
  ),
  update: (entityId: string, policy: PreferredCounterPolicy, edit: PreferredCounterEdit) => request(
    detail(entityId, policy.id), decodeReceipt,
    json('PUT', { ...edit, expectedRevision: policy.revision } satisfies Schemas['UpdatePrinterPolicyBody']),
  ),
  delete: (entityId: string, policy: Pick<PreferredCounterPolicy, 'id' | 'revision'>) => request(
    detail(entityId, policy.id), decodeReceipt,
    json('DELETE', { expectedRevision: policy.revision } satisfies Schemas['DeletePrinterPolicyBody']),
  ),
}
